import { Link, useParams } from 'react-router';
import {
  Avatar,
  Badge,
  Breadcrumb,
  Divider,
  EmptyState,
  ErrorState,
  InlineAlert,
  Metric,
  Panel,
  Progress,
  cn,
  PRESS,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPercentage,
  type BadgeTone,
} from 'escrowbuild-ui';

import { usePublishedPublications, useT, type AppTranslationKey } from '@/i18n';
import {
  ACCESS_LOG,
  findInvestor,
  findProject,
  PARTICIPATIONS,
  participationsForInvestor,
  type Investor,
  type Participation,
} from '@/data/domain';

const STATUS_TONE: Record<Investor['status'], BadgeTone> = {
  active: 'success',
  prospect: 'info',
  closed: 'neutral',
};

const STATUS_LABEL: Record<Investor['status'], AppTranslationKey> = {
  active: 'investor.status.active',
  prospect: 'status.investor.prospect',
  closed: 'status.investor.closed',
};

const KIND_LABEL: Record<Investor['kind'], AppTranslationKey> = {
  fund: 'investor.kind.fund',
  individual: 'status.investorKind.individual',
  trust: 'status.investorKind.trust',
};

const PARTICIPATION_TONE: Record<Participation['status'], BadgeTone> = {
  confirmed: 'success',
  pending: 'warning',
  draft: 'neutral',
};

/* The row badge used to print the wire code, and `confirmed` is the API's word
   rather than the reader's. These three are the shared status vocabulary's. */
const PARTICIPATION_LABEL: Record<Participation['status'], AppTranslationKey> = {
  confirmed: 'status.participation.confirmed',
  pending: 'status.participation.pending',
  draft: 'status.participation.draft',
};

const TH = 'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted';
const TD = 'px-3 py-2 text-body';

const LINK = cn(
  'font-medium text-heading underline decoration-line-strong underline-offset-2',
  'transition-colors duration-[var(--dur-fast)] hover:decoration-brand',
  PRESS,
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

/* The cell's link is a control of its own at roughly 13px of text, so it takes
   the 44px overlay — the email line beneath it is inside that target, which is
   where a user was going to click anyway. */
const CELL_LINK = cn(LINK, 'relative dhd-target');

export function InvestorDetailPage() {
  const t = useT();
  const { investorId } = useParams();
  const investor = findInvestor(investorId);

  /*
     Every released update, in the reader's language, resolved before the
     not-found return below because a hook cannot run after a conditional one —
     the empty investor's participation set selects nothing from it.
  */
  const releases = usePublishedPublications();

  if (!investor) {
    return (
      <Panel padding="lg" title={t('investor.notFoundTitle')}>
        <ErrorState
          variant="page"
          title={t('investor.notFoundHeading')}
          message={t('investor.notFoundMessage', { id: investorId ?? '' })}
        />
      </Panel>
    );
  }

  const participations = participationsForInvestor(investor.id);
  /** The developments this investor is in, for the released updates below. */
  const projectIds = new Set(participations.map((record) => record.projectId));
  // A record that is filtered out of the holdings is still a fact about this
  // investor — the pipeline entry that has not become a position yet.
  const pipelineOnly = PARTICIPATIONS.filter(
    (record) => record.investorId === investor.id && record.sharePercent === 0,
  );

  const committed = participations.reduce((sum, record) => sum + record.committed, 0);
  const contributed = participations.reduce((sum, record) => sum + record.contributed, 0);
  const outstanding = committed - contributed;
  const callsSettled = participations.reduce((sum, record) => sum + record.callsSettled, 0);
  const callsIssued = participations.reduce((sum, record) => sum + record.callsIssued, 0);
  const documents = participations.reduce((sum, record) => sum + record.documents, 0);

  /*
     Readership is assembled from both sides — the updates published to this
     investor and the access log — so an update that was released but never
     opened is as visible as one that was read. Showing only the log entries
     would make a silent investor look like a fully informed one.
  */
  const published = releases
    .filter((publication) => projectIds.has(publication.projectId))
    .map((publication) => ({
      publication,
      project: findProject(publication.projectId),
      access: ACCESS_LOG.find(
        (entry) => entry.investorId === investor.id && entry.publicationId === publication.id,
      ),
    }))
    .sort((a, b) => (b.publication.publishedAt ?? '').localeCompare(a.publication.publishedAt ?? ''));

  const opened = published.filter((entry) => entry.access !== undefined).length;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        items={[{ label: t('investors.title'), href: '/investors' }, { label: investor.name }]}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <Avatar name={investor.name} shape="square" size="xl" />
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
              {investor.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone="neutral" variant="subtle" size="sm">
                {t(KIND_LABEL[investor.kind])}
              </Badge>
              <Badge tone={STATUS_TONE[investor.status]} variant="subtle" size="sm">
                {t(STATUS_LABEL[investor.status])}
              </Badge>
              <Badge tone="neutral" variant="subtle" size="sm">
                {t('investor.joined', { date: formatDate(investor.joinedAt, { style: 'medium' }) })}
              </Badge>
            </div>
          </div>
        </div>

        <dl className="grid shrink-0 grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('investor.email')}
            </dt>
            <dd className="mt-0.5">
              <a href={`mailto:${investor.email}`} className={LINK}>
                {investor.email}
              </a>
            </dd>
          </div>
          <div>
            <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('investor.phone')}
            </dt>
            <dd className="mt-0.5">
              <a href={`tel:${investor.phone.replace(/[^+0-9]/g, '')}`} className={LINK}>
                {investor.phone}
              </a>
            </dd>
          </div>
          <div>
            <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('investor.city')}
            </dt>
            <dd className="mt-0.5 text-body">{investor.city}</dd>
          </div>
          <div>
            <dt className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
              {t('investor.taxId')}
            </dt>
            <dd className="mt-0.5 font-mono text-body">{investor.taxId}</dd>
          </div>
        </dl>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label={t('investor.developments')}
          value={participations.length}
          format="number"
          hint={participations.map((record) => findProject(record.projectId)?.name ?? '').join(' · ')}
        />
        <Metric label={t('investor.committed')} value={committed} format="money" />
        <Metric
          label={t('project.shell.contributed')}
          value={contributed}
          format="money"
          tone="info"
        />
        <Metric
          label={t('investor.outstanding')}
          value={outstanding}
          format="money"
          tone={outstanding === 0 ? 'success' : 'warning'}
          hint={t('investor.callsSettled', { count: callsIssued, settled: callsSettled })}
        />
      </div>

      <section className="rounded-xs border border-line bg-surface">
        <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line-subtle px-4 py-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold uppercase tracking-title text-heading">
              {t('investor.participations')}
            </h2>
            <p className="mt-1 text-sm text-muted">{t('investor.participationsHint')}</p>
          </div>
        </header>

        {participations.length === 0 ? (
          <div className="p-4">
            <EmptyState
              variant="cards"
              title={t('investor.emptyTitle')}
              description={
                pipelineOnly.length > 0
                  ? t('investor.emptyPipeline', {
                      name: investor.name,
                      projects: pipelineOnly
                        .map((record) => findProject(record.projectId)?.name ?? '')
                        .join(', '),
                    })
                  : t('investor.emptyNone', { name: investor.name })
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="dhd-sr-only">{t('investor.tableCaption')}</caption>
              <thead>
                <tr className="h-12 border-b-2 border-line-strong">
                  <th scope="col" className={cn(TH, 'text-left')}>{t('investor.col.development')}</th>
                  <th scope="col" className={cn(TH, 'text-left')}>{t('investor.col.joined')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('investor.col.share')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('investor.col.committed')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('project.shell.contributed')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('investor.col.outstanding')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('investor.col.calls')}</th>
                  <th scope="col" className={cn(TH, 'text-right')}>{t('investor.col.documents')}</th>
                  <th scope="col" className={cn(TH, 'text-left')}>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {participations.map((record) => {
                  const project = findProject(record.projectId);
                  return (
                    <tr key={record.id} className="h-12 border-b border-line-subtle hover:bg-surface-alt">
                      <th scope="row" className={cn(TD, 'text-left font-normal')}>
                        <Link to={`/projects/${record.projectId}/investors`} className={CELL_LINK}>
                          {project?.name ?? record.projectId}
                        </Link>
                        <span className="mt-0.5 block text-2xs text-muted">{project?.city}</span>
                      </th>
                      <td className={TD}>{formatDate(record.joinedAt)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {formatPercentage(record.sharePercent, { precision: 0 })}
                      </td>
                      <td className={cn(TD, 'text-right font-mono')}>{formatMoney(record.committed)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>{formatMoney(record.contributed)}</td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {formatMoney(record.committed - record.contributed)}
                      </td>
                      <td className={cn(TD, 'text-right font-mono')}>
                        {record.callsSettled} / {record.callsIssued}
                      </td>
                      <td className={cn(TD, 'text-right')}>{record.documents}</td>
                      <td className={TD}>
                        <Badge tone={PARTICIPATION_TONE[record.status]} variant="subtle" size="sm">
                          {t(PARTICIPATION_LABEL[record.status])}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line-strong bg-surface-alt">
                  <th
                    scope="row"
                    colSpan={3}
                    className="px-3 py-2 text-left font-display text-2xs font-semibold uppercase tracking-label text-heading"
                  >
                    <span className="dhd-sr-only">{t('common.total')} </span>
                    {t('investor.totalAcross', { count: participations.length })}
                  </th>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(committed)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(contributed)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {formatMoney(outstanding)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-heading">
                    {callsSettled} / {callsIssued}
                  </td>
                  <td className="px-3 py-2 text-right text-sm text-heading">{documents}</td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Panel
          title={t('investor.documentsPanel')}
          description={t('investor.documentsPanelHint')}
        >
          {participations.length === 0 ? (
            <p className="text-sm text-muted">{t('investor.noDocuments')}</p>
          ) : (
            <ul className="flex list-none flex-col gap-3 p-0">
              {participations.map((record) => (
                <li key={record.id} className="flex items-baseline justify-between gap-4">
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-body">
                      {findProject(record.projectId)?.name ?? record.projectId}
                    </span>
                    <span className="block text-2xs text-muted">{t('investor.documentKinds')}</span>
                  </span>
                  <span className="shrink-0 font-mono text-sm text-heading">{record.documents}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-2xs text-muted">
            {t('investor.documentsTotal', { count: documents })}
          </p>
        </Panel>

        <Panel
          title={t('investor.readPanel')}
          description={t('investor.readPanelHint', { opened, count: published.length })}
        >
          <InlineAlert
            tone="info"
            title={t('investor.readershipTitle')}
            description={t('investor.readershipBody')}
          />

          <Divider className="my-4" />

          {published.length === 0 ? (
            <p className="text-sm text-muted">
              {t('investor.nothingPublished', { name: investor.name })}
            </p>
          ) : (
            <ul className="flex list-none flex-col gap-4 p-0">
              {published.map((entry) => (
                <li key={entry.publication.id} className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-heading">
                        {entry.publication.title}
                      </span>
                      <span className="block text-2xs text-muted">
                        {entry.project?.name} · {entry.publication.period} ·{' '}
                        {t('investor.publishedOn', {
                          date: formatDate(entry.publication.publishedAt),
                        })}
                      </span>
                    </span>
                    {entry.access ? (
                      <Badge tone="success" variant="subtle" size="sm">
                        {t('investor.opened', { date: formatDateTime(entry.access.viewedAt) })}
                      </Badge>
                    ) : (
                      <Badge tone="warning" variant="subtle" size="sm">
                        {t('investor.notOpened')}
                      </Badge>
                    )}
                  </div>
                  {entry.access ? (
                    <Progress
                      value={entry.access.depth}
                      tone={entry.access.depth < 60 ? 'warning' : 'success'}
                      size="sm"
                      label={t('investor.readProgress', {
                        percent: formatPercentage(entry.access.depth, { precision: 0 }),
                      })}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
